'use client';

import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  WorkspaceReferenceImage,
  WorkspaceReferenceMedia,
} from '../contracts/runtime-state';
import { useWorkspaceImageMentionPreviews } from './use-workspace-image-mention-previews';
import {
  findAdjacentWorkspaceMentionChip,
  findWorkspaceEditableMentionQuery,
  getWorkspaceMentionChipRanges,
  readWorkspaceMentionEditorText,
  readWorkspaceMentionSelection,
  renderWorkspaceMentionEditor,
} from './workspace-image-mention-editor-dom';
import { buildWorkspaceMentionReferences } from './workspace-reference-mention';

/**
 * Owns native editing, atomic reference chips, caret offsets and the @ picker.
 * Its only persisted value is the canonical multimodal reference Prompt string.
 */
export function useWorkspaceImageMentionEditor(params: {
  prompt: string;
  images: WorkspaceReferenceImage[];
  media?: WorkspaceReferenceMedia[];
  disabled: boolean;
  onPromptChange: (nextPrompt: string) => void;
}) {
  const { prompt, images, media = [], disabled, onPromptChange } = params;
  const previewImages = useWorkspaceImageMentionPreviews(images);
  const mentionReferences = useMemo(
    () =>
      buildWorkspaceMentionReferences({
        images: previewImages,
        media,
      }),
    [media, previewImages]
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastRenderedPromptRef = useRef<string | null>(null);
  const lastRenderedReferencesKeyRef = useRef<string | null>(null);
  const lastCaretOffsetRef = useRef<number | null>(null);
  const isComposingRef = useRef(false);
  const activeRangeRef = useRef<{
    start: number;
    end: number;
    query: string;
  } | null>(null);
  const [activeRange, setActiveRange] =
    useState<typeof activeRangeRef.current>(null);
  const [activeOption, setActiveOption] = useState(0);

  // React callers may recreate the reference array on every keystroke.
  // Rebuild chips only when their actual identity, order or preview changes.
  const referencesKey = mentionReferences
    .map((reference) =>
      JSON.stringify([
        reference.id,
        reference.kind,
        reference.kindIndex,
        reference.previewUrl,
        reference.uploadStatus,
        reference.inputUrl,
      ])
    )
    .join('|');

  const filteredReferences = useMemo(() => {
    const query = activeRange?.query.toLowerCase() ?? '';
    return mentionReferences
      .map((reference, index) => ({ reference, index }))
      .filter(({ reference }) => {
        const compactLabel = reference.label.toLowerCase().replace(/\s+/g, '');
        return (
          reference.label.toLowerCase().includes(query) ||
          compactLabel.includes(query.replace(/\s+/g, ''))
        );
      });
  }, [activeRange?.query, mentionReferences]);

  const closeMenu = useCallback(() => {
    activeRangeRef.current = null;
    setActiveRange(null);
    setActiveOption(0);
  }, []);

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (
      lastRenderedPromptRef.current === prompt &&
      lastRenderedReferencesKeyRef.current === referencesKey
    ) {
      return;
    }

    const caret = readWorkspaceMentionSelection(editor)?.end;
    const desiredCaret =
      lastRenderedPromptRef.current === null
        ? prompt.length
        : Math.min(
            lastCaretOffsetRef.current ?? caret ?? prompt.length,
            prompt.length
          );
    renderWorkspaceMentionEditor({
      editor,
      prompt,
      references: mentionReferences,
      caretOffset: desiredCaret,
    });
    lastCaretOffsetRef.current = desiredCaret;
    lastRenderedPromptRef.current = prompt;
    lastRenderedReferencesKeyRef.current = referencesKey;
  }, [prompt, referencesKey, mentionReferences]);

  useEffect(() => {
    if (!activeRange) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closeMenu();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [activeRange, closeMenu]);

  const updateActiveQuery = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || mentionReferences.length === 0) {
      closeMenu();
      return;
    }
    const selection = readWorkspaceMentionSelection(editor);
    if (selection) lastCaretOffsetRef.current = selection.end;
    const currentPrompt = readWorkspaceMentionEditorText(editor);
    const range =
      selection && selection.start === selection.end
        ? findWorkspaceEditableMentionQuery(
            editor,
            currentPrompt,
            selection.end
          )
        : null;
    if (
      range &&
      !editor.querySelector(
        '.cms-block-workspace-workspace-1-workspace-image-mention__activeQuery'
      )
    ) {
      renderWorkspaceMentionEditor({
        editor,
        prompt: currentPrompt,
        references: mentionReferences,
        activeRange: range,
        caretOffset: selection?.end,
      });
    }
    activeRangeRef.current = range;
    setActiveRange(range);
    setActiveOption(0);
  }, [closeMenu, mentionReferences]);

  const commitPrompt = useCallback(
    (
      nextPrompt: string,
      caretOffset: number,
      options: { showPicker?: boolean } = {}
    ) => {
      const editor = editorRef.current;
      if (!editor) return;

      renderWorkspaceMentionEditor({
        editor,
        prompt: nextPrompt,
        references: mentionReferences,
        caretOffset,
      });

      const range =
        options.showPicker !== false && mentionReferences.length > 0
          ? findWorkspaceEditableMentionQuery(editor, nextPrompt, caretOffset)
          : null;
      if (range) {
        renderWorkspaceMentionEditor({
          editor,
          prompt: nextPrompt,
          references: mentionReferences,
          activeRange: range,
          caretOffset,
        });
      }

      activeRangeRef.current = range;
      setActiveRange(range);
      setActiveOption(0);
      lastRenderedPromptRef.current = nextPrompt;
      lastRenderedReferencesKeyRef.current = referencesKey;
      lastCaretOffsetRef.current = caretOffset;
      onPromptChange(nextPrompt);
    },
    [mentionReferences, onPromptChange, referencesKey]
  );

  const insertText = useCallback(
    (text: string) => {
      const editor = editorRef.current;
      if (!editor) return;
      const currentPrompt = readWorkspaceMentionEditorText(editor);
      const selection = readWorkspaceMentionSelection(editor) ?? {
        start: currentPrompt.length,
        end: currentPrompt.length,
      };
      const plain = text
        .replace(/[\u200b\u2060\ufeff]/g, '')
        .replace(/\r\n?/g, '\n');
      const nextPrompt =
        currentPrompt.slice(0, selection.start) +
        plain +
        currentPrompt.slice(selection.end);
      commitPrompt(nextPrompt, selection.start + plain.length);
    },
    [commitPrompt]
  );

  const deleteAtomicReference = useCallback(
    (direction: 'backward' | 'forward') => {
      const editor = editorRef.current;
      if (!editor) return false;
      const selection = readWorkspaceMentionSelection(editor);
      if (!selection) return false;

      const chips = getWorkspaceMentionChipRanges(editor);
      const chip =
        selection.start === selection.end
          ? findAdjacentWorkspaceMentionChip(editor, selection.start, direction)
          : chips.find(
              (item) => item.start < selection.end && item.end > selection.start
            );
      if (!chip) return false;

      const start =
        selection.start === selection.end ? chip.start : selection.start;
      const end = selection.start === selection.end ? chip.end : selection.end;
      const current = readWorkspaceMentionEditorText(editor);
      commitPrompt(current.slice(0, start) + current.slice(end), start, {
        showPicker: false,
      });
      return true;
    },
    [commitPrompt]
  );

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const onBeforeInput = (event: InputEvent) => {
      if (disabled || isComposingRef.current || event.isComposing) return;
      if (
        (event.inputType === 'deleteContentBackward' &&
          deleteAtomicReference('backward')) ||
        (event.inputType === 'deleteContentForward' &&
          deleteAtomicReference('forward'))
      ) {
        event.preventDefault();
      } else if (
        event.inputType === 'insertParagraph' ||
        event.inputType === 'insertLineBreak'
      ) {
        event.preventDefault();
        insertText('\n');
      }
    };
    editor.addEventListener('beforeinput', onBeforeInput);
    return () => editor.removeEventListener('beforeinput', onBeforeInput);
  }, [deleteAtomicReference, disabled, insertText]);

  const onInput = useCallback(() => {
    if (isComposingRef.current) return;
    const editor = editorRef.current;
    if (!editor) return;
    const next = readWorkspaceMentionEditorText(editor);
    const caret = readWorkspaceMentionSelection(editor)?.end ?? next.length;
    // Always rebuild using one canonical token parser. Native editing may
    // have changed the DOM to div/br or split a chip's text nodes.
    commitPrompt(next, caret);
  }, [commitPrompt]);

  const selectReference = useCallback(
    (referenceIndex: number) => {
      const editor = editorRef.current;
      const reference = mentionReferences[referenceIndex];
      if (!editor || !reference) return;
      const currentPrompt = readWorkspaceMentionEditorText(editor);
      const selection = readWorkspaceMentionSelection(editor);
      const range =
        activeRangeRef.current ??
        (selection
          ? findWorkspaceEditableMentionQuery(
              editor,
              currentPrompt,
              selection.end
            )
          : null);
      if (!range) return;

      const token = reference.token;
      const next =
        currentPrompt.slice(0, range.start) +
        token +
        currentPrompt.slice(range.end);
      closeMenu();
      commitPrompt(next, range.start + token.length, { showPicker: false });
    },
    [closeMenu, commitPrompt, mentionReferences]
  );

  const openMentionMenu = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || mentionReferences.length === 0 || disabled) return;
    const selectionBeforeFocus = readWorkspaceMentionSelection(editor);
    editor.focus();
    const currentPrompt = readWorkspaceMentionEditorText(editor);
    const caret =
      selectionBeforeFocus?.end ??
      lastCaretOffsetRef.current ??
      currentPrompt.length;
    const existing = findWorkspaceEditableMentionQuery(
      editor,
      currentPrompt,
      caret
    );
    if (existing) {
      activeRangeRef.current = existing;
      setActiveRange(existing);
      setActiveOption(0);
      renderWorkspaceMentionEditor({
        editor,
        prompt: currentPrompt,
        references: mentionReferences,
        activeRange: existing,
        caretOffset: caret,
      });
      return;
    }
    const next =
      currentPrompt.slice(0, caret) + '@' + currentPrompt.slice(caret);
    commitPrompt(next, caret + 1);
  }, [commitPrompt, disabled, mentionReferences]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isComposingRef.current || event.nativeEvent.isComposing) return;

    if (activeRange && filteredReferences.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveOption((value) => (value + 1) % filteredReferences.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveOption(
          (value) =>
            (value - 1 + filteredReferences.length) % filteredReferences.length
        );
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const reference =
          filteredReferences[activeOption] ?? filteredReferences[0];
        if (reference) selectReference(reference.index);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }
    }

    if (
      (event.key === 'Backspace' && deleteAtomicReference('backward')) ||
      (event.key === 'Delete' && deleteAtomicReference('forward'))
    ) {
      event.preventDefault();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      insertText('\n');
    }
  };

  return {
    rootRef,
    editorRef,
    activeRange,
    activeOption,
    filteredReferences,
    closeMenu,
    openMentionMenu,
    selectReference,
    updateActiveQuery,
    handleKeyDown,
    onInput,
    onPaste: insertText,
    onCompositionStart: () => {
      isComposingRef.current = true;
    },
    onCompositionEnd: () => {
      isComposingRef.current = false;
      onInput();
    },
  };
}
